import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ChangeEvent } from 'react'
import { Maximize } from 'lucide-react'
import { getCollageTemplate } from '@/content/collageTemplates'
import { getFilmFormat } from '@/content/instantFilm'
import { OutputPresetId, getOutputPreset, gridVisibleRect } from '@/content/instagram'
import {
  NodeKind,
  SplitKind,
  TransformKind,
  collageCellContext,
  collageCellRect,
  containsFrameStyle,
  documentOutputRatio,
  firstImageNode,
  imageWindows,
  imageNodesInOrder,
  nativeSize,
  outputContainLayout,
  parentNode,
  type CompositionNode,
  type Document,
  type FrameNode,
} from '@/core/document'
import { IMAGE_FILE_ACCEPT } from '@/core/image'
import {
  EDITOR_PADDING_PX,
  FIT_VIEW_TRANSFORM,
  VIEW_ZOOM_MIN,
  clampViewTransform,
  composeFitAndViewTransform,
  fitTransform,
  type AffineMatrix,
  type Handle,
  type Size,
} from '@/core/geometry'
import { renderNode } from '@/core/render/renderNode'
import { RenderUnsupportedError } from '@/core/render/errors'
import { ReportingStage, surfaceError } from '@/core/reporting'
import { useEditSession } from '@/core/store/editSession'
import {
  BOTTOM_SHEET_SNAP_TRANSITION,
  BottomSheetSnap,
  OverlayId,
  ToolTab,
  bottomSheetSnapHeightPx,
} from '@/core/store/uiState'
import { TEST_IDS } from '@/lib/testIds'
import { useIsCoarsePointer } from '@/lib/usePointerType'
import { cn } from '@/lib/utils'
import { fillSelectedCollageFiles } from '@/tools/collage/actions'
import { useCollageCellSwap } from '@/tools/collage/useCollageCellSwap'
import { FrameGestureMode, useFrameGestures } from '@/tools/crop/gestures/useFrameGestures'
import { ensureFrameFontsLoaded } from '@/tools/frame/fonts'
import { FrameStyleId, PhotoPlacement, type PolaroidFrameParams } from '@/tools/frame/params'
import { Toggle } from '@/ui/shadcn/toggle'
import { Button } from '@/ui/shadcn/button'

const HANDLE_POSITIONS: Readonly<Record<Handle, string>> = {
  'top-left': '-left-2 -top-2 cursor-nwse-resize',
  top: 'left-1/2 -top-2 -translate-x-1/2 cursor-ns-resize',
  'top-right': '-right-2 -top-2 cursor-nesw-resize',
  right: '-right-2 top-1/2 -translate-y-1/2 cursor-ew-resize',
  'bottom-right': '-bottom-2 -right-2 cursor-nwse-resize',
  bottom: '-bottom-2 left-1/2 -translate-x-1/2 cursor-ns-resize',
  'bottom-left': '-bottom-2 -left-2 cursor-nesw-resize',
  left: '-left-2 top-1/2 -translate-y-1/2 cursor-ew-resize',
}

const ACTIVE_PHOTO_OUTLINE = {
  outline: '2px solid var(--primary)',
  boxShadow: '0 0 0 3px rgb(255 255 255 / 0.9)',
} satisfies CSSProperties

const ACTIVE_PHOTO_CORNER_CLASSES = [
  '-left-1 -top-1 border-l-2 border-t-2',
  '-right-1 -top-1 border-r-2 border-t-2',
  '-bottom-1 -right-1 border-b-2 border-r-2',
  '-bottom-1 -left-1 border-b-2 border-l-2',
] as const

interface VisualRegion {
  width: number
  height: number
  rotationDeg: number
  style: CSSProperties
  corners: readonly { x: number; y: number }[]
}

interface RenderFailure {
  document: Document
  error: Error
}

interface ViewportMeasurement {
  editor: Size
  dynamicViewportHeight: number
  rootFontSize: number
}

type PolaroidFrameNode = Omit<FrameNode, 'params'> & { params: PolaroidFrameParams }

function isPolaroidFrame(node: CompositionNode | null): node is PolaroidFrameNode {
  return node?.kind === NodeKind.Frame && node.params.styleId === FrameStyleId.Polaroid
}

function multiply(first: AffineMatrix, second: AffineMatrix): AffineMatrix {
  return {
    a: first.a * second.a + first.c * second.b,
    b: first.b * second.a + first.d * second.b,
    c: first.a * second.c + first.c * second.d,
    d: first.b * second.c + first.d * second.d,
    e: first.a * second.e + first.c * second.f + first.e,
    f: first.b * second.e + first.d * second.f + first.f,
  }
}

function translate(x: number, y: number): AffineMatrix {
  return { a: 1, b: 0, c: 0, d: 1, e: x, f: y }
}

function rotate(degrees: number): AffineMatrix {
  const radians = degrees * Math.PI / 180
  return { a: Math.cos(radians), b: Math.sin(radians), c: -Math.sin(radians), d: Math.cos(radians), e: 0, f: 0 }
}

function mapPoint(matrix: AffineMatrix, point: { x: number; y: number }) {
  return {
    x: matrix.a * point.x + matrix.c * point.y + matrix.e,
    y: matrix.b * point.x + matrix.d * point.y + matrix.f,
  }
}

function visualRegion(size: Size, matrix: AffineMatrix, viewportTransform: ReturnType<typeof fitTransform>): VisualRegion {
  const screenMatrix: AffineMatrix = {
    a: matrix.a * viewportTransform.scale,
    b: matrix.b * viewportTransform.scale,
    c: matrix.c * viewportTransform.scale,
    d: matrix.d * viewportTransform.scale,
    e: matrix.e * viewportTransform.scale + viewportTransform.offsetX,
    f: matrix.f * viewportTransform.scale + viewportTransform.offsetY,
  }
  const scale = Math.hypot(screenMatrix.a, screenMatrix.b)
  if (!Number.isFinite(scale) || scale <= 0) throw new Error('Composition photo window has an invalid screen scale')
  const normalized: AffineMatrix = {
    a: screenMatrix.a / scale,
    b: screenMatrix.b / scale,
    c: screenMatrix.c / scale,
    d: screenMatrix.d / scale,
    e: screenMatrix.e,
    f: screenMatrix.f,
  }
  const width = size.width * scale
  const height = size.height * scale
  return {
    width,
    height,
    rotationDeg: Math.atan2(normalized.b, normalized.a) * 180 / Math.PI,
    style: {
      width,
      height,
      transformOrigin: '0 0',
      transform: `matrix(${normalized.a}, ${normalized.b}, ${normalized.c}, ${normalized.d}, ${normalized.e}, ${normalized.f})`,
    },
    corners: [
      mapPoint(normalized, { x: 0, y: 0 }),
      mapPoint(normalized, { x: width, y: 0 }),
      mapPoint(normalized, { x: width, y: height }),
      mapPoint(normalized, { x: 0, y: height }),
    ],
  }
}

function gestureMode(tab: ToolTab, freePlacement: boolean): FrameGestureMode {
  switch (tab) {
    case ToolTab.Crop:
      return FrameGestureMode.Crop
    case ToolTab.Adjust:
      return FrameGestureMode.None
    case ToolTab.Frame:
      return freePlacement ? FrameGestureMode.FreePlacement : FrameGestureMode.None
    case ToolTab.Collage:
      return FrameGestureMode.Collage
    case ToolTab.Format:
    case ToolTab.Export:
      return FrameGestureMode.None
    default:
      throw new Error(`Unknown active tool tab: "${String(tab)}"`)
  }
}

export function CompositionEditor() {
  const containerRef = useRef<HTMLDivElement>(null)
  const visualWrapperRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const collageInputRef = useRef<HTMLInputElement>(null)
  const requestedCellIndex = useRef<number | undefined>(undefined)
  const fitAnimationRequested = useRef(false)
  const initialViewportSize = EDITOR_PADDING_PX * 2 + 1
  const previousViewportTransform = useRef<{ snap: BottomSheetSnap; transform: ReturnType<typeof fitTransform> } | null>(null)
  const [measurement, setMeasurement] = useState<ViewportMeasurement>({
    editor: { width: initialViewportSize, height: initialViewportSize },
    dynamicViewportHeight: initialViewportSize,
    rootFontSize: 16,
  })
  const [renderFailure, setRenderFailure] = useState<RenderFailure | null>(null)
  const isCoarsePointer = useIsCoarsePointer()
  const document = useEditSession((state) => state.document)
  const sources = useEditSession((state) => state.sources)
  const selectedNodeId = useEditSession((state) => state.selectedNodeId)
  const activeTab = useEditSession((state) => state.activeTab)
  const viewTransform = useEditSession((state) => state.viewTransform)
  const handlesVisible = useEditSession((state) => state.handlesVisible)
  const mobileSheetSnap = useEditSession((state) => state.mobileSheetSnap)
  const safeZonesVisible = useEditSession((state) => state.overlaysVisible.has(OverlayId.SafeZones))
  const gridAreaVisible = useEditSession((state) => state.overlaysVisible.has(OverlayId.Grid))
  const selectNode = useEditSession((state) => state.selectNode)
  const setViewTransform = useEditSession((state) => state.setViewTransform)
  const setOverlayVisible = useEditSession((state) => state.setOverlayVisible)
  if (!document) throw new Error('Composition editor requires a loaded document')

  const viewport = useMemo(() => {
    if (!isCoarsePointer) return measurement.editor
    const fitSnap = mobileSheetSnap === BottomSheetSnap.Full
      ? BottomSheetSnap.Half
      : mobileSheetSnap
    const sheetHeight = bottomSheetSnapHeightPx(
      fitSnap,
      measurement.dynamicViewportHeight,
      measurement.rootFontSize,
    )
    return {
      width: measurement.editor.width,
      height: Math.max(initialViewportSize, measurement.editor.height - sheetHeight),
    }
  }, [initialViewportSize, isCoarsePointer, measurement, mobileSheetSnap])

  const rootSize = useMemo(() => nativeSize(document, sources, document.root.id), [document, sources])
  const resolvedOutputRatio = useMemo(() => documentOutputRatio(document, sources), [document, sources])
  const outputLayout = useMemo(() => outputContainLayout(rootSize, resolvedOutputRatio), [resolvedOutputRatio, rootSize])
  const fitViewportTransform = useMemo(() => fitTransform(outputLayout.size, viewport), [outputLayout.size, viewport])
  const clampedViewTransform = useMemo(
    () => clampViewTransform(viewTransform, fitViewportTransform, outputLayout.size, viewport),
    [fitViewportTransform, outputLayout.size, viewTransform, viewport],
  )
  const viewportTransform = useMemo(
    () => composeFitAndViewTransform(fitViewportTransform, clampedViewTransform),
    [clampedViewTransform, fitViewportTransform],
  )

  useLayoutEffect(() => {
    if (clampedViewTransform.scale === viewTransform.scale
      && clampedViewTransform.offsetX === viewTransform.offsetX
      && clampedViewTransform.offsetY === viewTransform.offsetY) return
    setViewTransform(clampedViewTransform)
  }, [clampedViewTransform, setViewTransform, viewTransform])

  useLayoutEffect(() => {
    const wrapper = visualWrapperRef.current
    if (!wrapper) throw new Error('Composition visual wrapper is missing')
    const current = { snap: mobileSheetSnap, transform: viewportTransform }
    const previous = previousViewportTransform.current
    previousViewportTransform.current = current
    const animateFit = fitAnimationRequested.current
    fitAnimationRequested.current = false
    const animateSheet = isCoarsePointer && previous?.snap !== mobileSheetSnap
    if (!previous || (!animateFit && !animateSheet)) return

    const scale = previous.transform.scale / viewportTransform.scale
    const translateX = previous.transform.offsetX - viewportTransform.offsetX * scale
    const translateY = previous.transform.offsetY - viewportTransform.offsetY * scale
    if (Math.abs(scale - 1) < 0.0001 && Math.abs(translateX) < 0.01 && Math.abs(translateY) < 0.01) return

    wrapper.style.transition = 'none'
    wrapper.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`
    wrapper.getBoundingClientRect()
    const animationFrame = window.requestAnimationFrame(() => {
      const durationMs = animateFit ? 150 : BOTTOM_SHEET_SNAP_TRANSITION.durationMs
      wrapper.style.transition = `transform ${durationMs}ms ${BOTTOM_SHEET_SNAP_TRANSITION.timingFunction}`
      wrapper.style.transform = 'translate(0, 0) scale(1)'
    })
    return () => window.cancelAnimationFrame(animationFrame)
  }, [isCoarsePointer, mobileSheetSnap, viewportTransform])
  const windows = useMemo(
    () => imageWindows(document, sources, rootSize, outputLayout.rootRect),
    [document, outputLayout.rootRect, rootSize, sources],
  )
  const photoNumbers = useMemo(() => new Map(
    imageNodesInOrder(document.root).map((photo, index) => [photo.id, index + 1]),
  ), [document.root])
  const hasMultiplePhotos = photoNumbers.size > 1
  const activeWindow = windows.find((window) => window.image.id === selectedNodeId) ?? null
  if (selectedNodeId && !activeWindow) throw new Error(`Active photo "${selectedNodeId}" has no rendered composition window`)
  const activeVisual = activeWindow ? visualRegion(activeWindow.size, activeWindow.transform, viewportTransform) : null
  const activeImage = activeWindow?.image ?? null
  const activeCrop = activeImage?.transforms[TransformKind.Crop] ?? null
  if (activeImage && !activeCrop) throw new Error(`Active photo "${activeImage.id}" has no configured crop`)
  const parent = activeImage ? parentNode(document.root, activeImage.id) : null
  const polaroidParent = isPolaroidFrame(parent) ? parent : null
  const isFreePlacement = polaroidParent?.params.placement === PhotoPlacement.Free
  const freeWindowVisual = isFreePlacement && activeWindow && polaroidParent?.params.free
    ? (() => {
        const format = getFilmFormat(polaroidParent.params.formatId)
        const windowWidth = activeWindow.size.width / polaroidParent.params.free.scale
        const windowHeight = windowWidth * format.windowMm.height / format.windowMm.width
        const photoX = windowWidth * (0.5 + polaroidParent.params.free.offsetX) - activeWindow.size.width / 2
        const photoY = windowHeight * (0.5 + polaroidParent.params.free.offsetY) - activeWindow.size.height / 2
        return visualRegion(
          { width: windowWidth, height: windowHeight },
          multiply(activeWindow.transform, translate(-photoX, -photoY)),
          viewportTransform,
        )
      })()
    : null
  const mode = gestureMode(activeTab, isFreePlacement)
  const cropGesture = mode === FrameGestureMode.Crop
  const freeGesture = mode === FrameGestureMode.FreePlacement
  const gestureEnabled = activeVisual !== null && (cropGesture || freeGesture)
  const placementWidth = freeWindowVisual?.width
  const placementHeight = freeWindowVisual?.height
  const placementWindowSize = useMemo(() => {
    if (!placementWidth || !placementHeight) return undefined
    return { width: placementWidth, height: placementHeight }
  }, [placementHeight, placementWidth])
  const photoWindowSize = useMemo(() => activeVisual
    ? { width: activeVisual.width, height: activeVisual.height }
    : undefined, [activeVisual])
  const photoWindowCenter = useMemo(() => activeVisual
    ? {
        x: activeVisual.corners.reduce((sum, point) => sum + point.x, 0) / activeVisual.corners.length,
        y: activeVisual.corners.reduce((sum, point) => sum + point.y, 0) / activeVisual.corners.length,
      }
    : undefined, [activeVisual])

  const gestures = useFrameGestures({
    viewport,
    compositionSize: outputLayout.size,
    fitTransform: fitViewportTransform,
    editorRef: containerRef,
    mode,
    photoWindowSize,
    photoWindowCenter,
    placementWindowSize,
    rotationDeg: activeVisual?.rotationDeg,
  })
  const { isInteracting, ...gestureHandlers } = gestures

  const fitView = () => {
    fitAnimationRequested.current = true
    setViewTransform({ ...FIT_VIEW_TRANSFORM })
  }

  const chooseCellFiles = (cellIndex: number) => {
    requestedCellIndex.current = cellIndex
    const input = collageInputRef.current
    if (!input) throw new Error('Collage cell file picker is not configured')
    input.click()
  }
  const activateCollageCell = (cellIndex: number) => {
    if (document.root.kind !== NodeKind.Collage) throw new Error('Collage cell activation requires a Collage document')
    const child = document.root.cells[cellIndex]
    if (child) selectNode(firstImageNode(child).id)
    else chooseCellFiles(cellIndex)
  }
  const cellSwap = useCollageCellSwap(activateCollageCell)

  useEffect(() => {
    const container = containerRef.current
    if (!container) throw new Error('Composition editor container is missing')
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) throw new Error('Composition editor resize entry is missing')
      const rootFontSize = Number.parseFloat(window.getComputedStyle(window.document.documentElement).fontSize)
      if (!Number.isFinite(rootFontSize) || rootFontSize <= 0) {
        throw new Error('Composition editor requires a finite positive root font size')
      }
      setMeasurement({
        editor: {
          width: Math.max(1, entry.contentRect.width),
          height: Math.max(1, entry.contentRect.height),
        },
        dynamicViewportHeight: window.innerHeight,
        rootFontSize,
      })
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) throw new Error('Composition preview canvas is missing')
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Composition preview requires a 2D canvas context')
    const dpr = window.devicePixelRatio || 1
    canvas.width = Math.max(1, Math.round(viewport.width * dpr))
    canvas.height = Math.max(1, Math.round(viewport.height * dpr))
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`
    context.setTransform(dpr, 0, 0, dpr, 0, 0)
    context.clearRect(0, 0, viewport.width, viewport.height)
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
    let cancelled = false
    const fontsReady = containsFrameStyle(document.root, FrameStyleId.Polaroid)
      ? ensureFrameFontsLoaded()
      : Promise.resolve()
    void fontsReady.then(() => {
      if (cancelled) return
      const previewSources = new Map([...sources].map(([id, source]) => [id, { ...source, bitmap: source.previewBitmap }]))
      context.save()
      context.translate(viewportTransform.offsetX, viewportTransform.offsetY)
      context.scale(viewportTransform.scale, viewportTransform.scale)
      context.fillStyle = document.output.background
      context.fillRect(0, 0, outputLayout.size.width, outputLayout.size.height)
      context.translate(outputLayout.rootRect.x, outputLayout.rootRect.y)
      context.beginPath()
      context.rect(0, 0, rootSize.width, rootSize.height)
      context.clip()
      renderNode(document.root, document, previewSources, rootSize, context)
      context.restore()
    }).catch((error: unknown) => {
      if (!cancelled) {
        setRenderFailure({
          document,
          error: error instanceof Error ? error : new Error('Composition preview failed'),
        })
      }
    })
    return () => {
      cancelled = true
    }
  }, [document, outputLayout, rootSize, sources, viewport, viewportTransform])

  const renderError = renderFailure?.document === document ? renderFailure.error : null
  if (renderError && !(renderError instanceof RenderUnsupportedError)) throw renderError
  const unsupportedMessage = renderError instanceof RenderUnsupportedError
    ? renderError.message
    : null

  const fillCells = (event: ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.currentTarget.files ?? [])]
    event.currentTarget.value = ''
    const cellIndex = requestedCellIndex.current
    requestedCellIndex.current = undefined
    if (files.length === 0) return
    void fillSelectedCollageFiles(files, cellIndex).catch((error: unknown) => {
      surfaceError(error, 'Could not add photos to the collage.', { stage: ReportingStage.SourceLoad })
    })
  }

  const visualWindows = windows.map((window) => ({
    window,
    visual: visualRegion(window.size, window.transform, viewportTransform),
    cell: collageCellContext(document.root, window.image.id),
  }))
  const emptyCells = document.root.kind === NodeKind.Collage
    ? (() => {
        const template = getCollageTemplate(document.root.templateId)
        const rootTransform = translate(outputLayout.rootRect.x, outputLayout.rootRect.y)
        return template.cells.flatMap((cell, index) => {
          if (document.root.kind !== NodeKind.Collage || document.root.cells[index]) return []
          const rect = collageCellRect(template, cell, rootSize, document.root.gap)
          const matrix = multiply(
            rootTransform,
            multiply(
              translate(rect.x + rect.width / 2, rect.y + rect.height / 2),
              multiply(rotate(cell.rotationDeg), translate(-rect.width / 2, -rect.height / 2)),
            ),
          )
          return [{ index, z: cell.z, visual: visualRegion({ width: rect.width, height: rect.height }, matrix, viewportTransform) }]
        })
      })()
    : []
  const showCropOverlay = activeTab === ToolTab.Crop && activeVisual !== null
  const showCropGuideGrid = showCropOverlay
  const showHandles = showCropOverlay && document.root.id === activeImage?.id && handlesVisible
  const compositionStyle: CSSProperties = {
    left: viewportTransform.offsetX,
    top: viewportTransform.offsetY,
    width: outputLayout.size.width * viewportTransform.scale,
    height: outputLayout.size.height * viewportTransform.scale,
  }
  const preset = getOutputPreset(document.output.presetId)
  const split = document.output.split
  const gridArea = gridVisibleRect(outputLayout.size)
  const gridOverlayAvailable = activeTab === ToolTab.Crop
    && split === null
    && preset.id !== OutputPresetId.Story
  const carouselTiles = split?.kind === SplitKind.Carousel ? split.tiles : null
  const gridRows = split?.kind === SplitKind.Grid ? split.rows : null

  return (
    <div
      ref={containerRef}
      data-testid={TEST_IDS.compositionEditor}
      data-view-scale={clampedViewTransform.scale}
      data-view-offset-x={clampedViewTransform.offsetX}
      data-view-offset-y={clampedViewTransform.offsetY}
      className="relative h-full min-h-72 w-full overflow-hidden bg-neutral-950 touch-none"
      aria-label="Composition editor"
      {...gestureHandlers}
    >
      {document.root.kind === NodeKind.Collage ? (
        <input ref={collageInputRef} type="file" multiple className="sr-only" accept={IMAGE_FILE_ACCEPT} onChange={fillCells} aria-label="Choose collage photos" />
      ) : null}
      <div ref={visualWrapperRef} className="absolute inset-0 origin-top-left">
        <canvas ref={canvasRef} data-testid={TEST_IDS.previewCanvas} className="absolute inset-0" aria-label="Composition preview" />
      {unsupportedMessage ? (
        <div
          role="alert"
          className="absolute inset-0 z-40 flex items-center justify-center bg-neutral-950 p-6 text-center text-sm text-white"
        >
          {unsupportedMessage}
        </div>
      ) : null}

      {activeTab === ToolTab.Collage ? emptyCells.map((cell) => (
        <button
          key={cell.index}
          type="button"
          aria-label={`Add photo to collage cell ${cell.index + 1}`}
          data-testid={TEST_IDS.collageCell(cell.index)}
          data-collage-cell-index={cell.index}
          data-collage-cell-filled="false"
          {...cellSwap.handlers}
          className="absolute z-10 flex items-center justify-center border-2 border-dashed border-white/80 bg-neutral-950/30 text-2xl font-light text-white outline-offset-2 hover:bg-neutral-950/45"
          style={{ ...cell.visual.style, zIndex: 10 + cell.z }}
        >
          <span aria-hidden="true">+</span>
        </button>
      )) : null}

      {visualWindows.map(({ window, visual, cell }) => {
        const active = window.image.id === selectedNodeId
        const photoNumber = photoNumbers.get(window.image.id)
        if (photoNumber === undefined) throw new Error(`Rendered photo "${window.image.id}" has no reading-order number`)
        const cellIndex = cell?.cellIndex
        const collageInteraction = activeTab === ToolTab.Collage && cellIndex !== undefined
        return (
          <button
            key={window.image.id}
            type="button"
            aria-label={active ? 'Active photo' : 'Make photo active'}
            aria-pressed={active}
            data-testid={cellIndex === undefined ? undefined : TEST_IDS.collageCell(cellIndex)}
            data-collage-cell-index={cellIndex}
            data-collage-cell-filled={cellIndex === undefined ? undefined : 'true'}
            data-canvas-photo="true"
            data-canvas-photo-active={active ? 'true' : 'false'}
            {...(collageInteraction ? cellSwap.handlers : { onClick: () => selectNode(window.image.id) })}
            className={cn(
              'absolute z-10 bg-transparent outline-offset-2',
              activeTab === ToolTab.Collage && 'border border-white/65',
              cellSwap.dragSourceIndex === cellIndex && 'opacity-65 ring-2 ring-white',
              cellSwap.dropTargetIndex === cellIndex && 'ring-4 ring-primary',
            )}
            style={visual.style}
          >
            <span className="sr-only">Photo</span>
            {activeTab === ToolTab.Collage && hasMultiplePhotos && !active ? (
              <span className="absolute -left-2.5 -top-2.5 z-10 flex size-5 items-center justify-center rounded-full bg-neutral-600/90 text-[11px] font-bold leading-none text-white shadow-sm" aria-hidden="true">
                {photoNumber}
              </span>
            ) : null}
          </button>
        )
      })}

      {showCropOverlay && activeVisual ? (
        <svg className="pointer-events-none absolute inset-0 z-20 h-full w-full" aria-hidden="true">
          <path
            d={`M0 0H${viewport.width}V${viewport.height}H0Z M${activeVisual.corners.map((point) => `${point.x} ${point.y}`).join('L')}Z`}
            fill="rgb(0 0 0 / 0.58)"
            fillRule="evenodd"
          />
        </svg>
      ) : null}

      {gridOverlayAvailable && gridAreaVisible ? (
        <div className="pointer-events-none absolute z-20" style={compositionStyle}>
          <div
            data-testid={TEST_IDS.gridOverlay}
            className="absolute border-2 border-dashed border-white/90 bg-white/5"
            style={{
              left: `${gridArea.x / outputLayout.size.width * 100}%`,
              top: `${gridArea.y / outputLayout.size.height * 100}%`,
              width: `${gridArea.width / outputLayout.size.width * 100}%`,
              height: `${gridArea.height / outputLayout.size.height * 100}%`,
            }}
          />
        </div>
      ) : null}

      {activeVisual && gestureEnabled ? (
        <div
          data-testid={TEST_IDS.activePhotoWindow}
          data-canvas-photo="true"
          data-canvas-photo-active="true"
          className={cn(
            'absolute z-30 cursor-grab active:cursor-grabbing',
            cropGesture && 'border border-white/90',
          )}
          style={activeVisual.style}
        >
          {showCropGuideGrid ? (
            <div className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3 overflow-hidden" aria-hidden="true">
              {Array.from({ length: 9 }, (_, index) => <span key={index} className={cn('border-white/45', index % 3 !== 2 && 'border-r', index < 6 && 'border-b')} />)}
            </div>
          ) : null}
          {showHandles ? Object.entries(HANDLE_POSITIONS).map(([handle, position]) => (
            <button key={handle} type="button" data-handle={handle} aria-label={`Resize crop from ${handle}`} className={cn('absolute size-4 rounded-full border-2 border-neutral-950 bg-white shadow-sm', position)} />
          )) : null}
          {isInteracting ? <span className="sr-only">Editing photo</span> : null}
        </div>
      ) : activeVisual && hasMultiplePhotos ? (
        <div data-testid={TEST_IDS.activePhotoWindow} className="pointer-events-none absolute z-20" style={activeVisual.style} />
      ) : null}

      {activeVisual && activeImage && hasMultiplePhotos && !unsupportedMessage ? (
        <div
          key={activeImage.id}
          className="pointer-events-none absolute z-40 overflow-visible"
          style={activeVisual.style}
          data-testid={TEST_IDS.activePhotoMarker}
          data-photo-number={photoNumbers.get(activeImage.id)}
          aria-hidden="true"
        >
          <div className="active-photo-pulse absolute inset-0 overflow-visible" style={ACTIVE_PHOTO_OUTLINE}>
            {ACTIVE_PHOTO_CORNER_CLASSES.map((classes) => (
              <span key={classes} className={cn('absolute size-3.5 border-primary', classes)} />
            ))}
            <span
              className="absolute -left-2.5 -top-2.5 z-10 flex size-5 items-center justify-center rounded-full bg-primary text-[11px] font-bold leading-none text-white shadow-sm"
              data-testid={TEST_IDS.activePhotoBadge}
            >
              {photoNumbers.get(activeImage.id)}
            </span>
          </div>
        </div>
      ) : null}

      {activeTab === ToolTab.Frame && freeWindowVisual ? (
        <div className="pointer-events-none absolute z-30 border border-primary" style={freeWindowVisual.style} aria-hidden="true" />
      ) : null}

      {activeTab === ToolTab.Format ? (
        <div className="pointer-events-none absolute z-20" style={compositionStyle}>
          {carouselTiles ? Array.from({ length: carouselTiles - 1 }, (_, index) => (
            <span key={index} className="absolute inset-y-0 border-l border-dashed border-white/80" style={{ left: `${((index + 1) / carouselTiles) * 100}%` }} />
          )) : null}
          {gridRows ? (
            <>
              {[1, 2].map((column) => <span key={`c${column}`} className="absolute inset-y-0 border-l border-dashed border-white/80" style={{ left: `${column / 3 * 100}%` }} />)}
              {Array.from({ length: gridRows - 1 }, (_, index) => <span key={`r${index}`} className="absolute inset-x-0 border-t border-dashed border-white/80" style={{ top: `${(index + 1) / gridRows * 100}%` }} />)}
            </>
          ) : null}
          {safeZonesVisible && preset.safeZones ? preset.safeZones.map((zone) => (
            <div key={zone.id} data-testid={TEST_IDS.safeZonesOverlay} className="absolute flex items-center justify-center border-y border-white/20 bg-neutral-950/55 px-2 text-center text-[10px] text-white/75" style={{ left: `${zone.rect.x * 100}%`, top: `${zone.rect.y * 100}%`, width: `${zone.rect.width * 100}%`, height: `${zone.rect.height * 100}%` }}>{zone.label}</div>
          )) : null}
        </div>
      ) : null}
      </div>

      {clampedViewTransform.scale > VIEW_ZOOM_MIN && !unsupportedMessage ? (
        <Button
          type="button"
          variant="secondary"
          size="icon"
          data-testid={TEST_IDS.canvasFit}
          data-canvas-control="true"
          className="absolute right-3 z-50 shadow-md"
          style={{ top: Math.max(12, viewport.height - 48) }}
          onClick={fitView}
          aria-label="Fit canvas"
          title="Fit canvas"
        >
          <Maximize aria-hidden="true" />
        </Button>
      ) : null}

      {activeTab === ToolTab.Format && preset.safeZones ? (
        <Toggle type="button" variant="outline" size="sm" pressed={safeZonesVisible} onPressedChange={(pressed) => setOverlayVisible(OverlayId.SafeZones, pressed)} data-testid={TEST_IDS.safeZonesToggle} data-canvas-control="true" className="absolute right-3 top-3 z-30 bg-background/90 shadow-sm" aria-label="Show Story safe zones">Safe zones</Toggle>
      ) : null}

      {gridOverlayAvailable ? (
        <Toggle type="button" variant="outline" size="sm" pressed={gridAreaVisible} onPressedChange={(pressed) => setOverlayVisible(OverlayId.Grid, pressed)} data-testid={TEST_IDS.gridToggle} data-canvas-control="true" className="absolute right-3 top-3 z-30 bg-background/90 shadow-sm" aria-label="Show profile grid area">Grid area</Toggle>
      ) : null}
    </div>
  )
}
